/**
 * Modal Composable
 * Provides a simple API for showing modals throughout the application
 */

import { ref, reactive } from 'vue'

export interface ModalOptions {
  title?: string
  message?: string
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showClose?: boolean
  showFooter?: boolean
  showCancel?: boolean
  confirmText?: string
  cancelText?: string
  closeOnOverlay?: boolean
  icon?: string
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void
  onClose?: () => void
}

const modalState = reactive({
  isOpen: false,
  title: '',
  message: '',
  variant: 'default' as const,
  size: 'md' as const,
  showClose: true,
  showFooter: true,
  showCancel: true,
  confirmText: 'OK',
  cancelText: 'Cancel',
  closeOnOverlay: true,
  icon: undefined as string | undefined,
  loading: false,
  onConfirm: undefined as (() => void | Promise<void>) | undefined,
  onCancel: undefined as (() => void) | undefined,
  onClose: undefined as (() => void) | undefined,
})

export function useModal() {
  function show(options: ModalOptions | string) {
    // If string is passed, treat as message
    if (typeof options === 'string') {
      modalState.message = options
      modalState.title = undefined
    } else {
      Object.assign(modalState, {
        title: options.title,
        message: options.message,
        variant: options.variant || 'default',
        size: options.size || 'md',
        showClose: options.showClose !== false,
        showFooter: options.showFooter !== false,
        showCancel: options.showCancel !== false,
        confirmText: options.confirmText || 'OK',
        cancelText: options.cancelText || 'Cancel',
        closeOnOverlay: options.closeOnOverlay !== false,
        icon: options.icon,
        onConfirm: options.onConfirm,
        onCancel: options.onCancel,
        onClose: options.onClose,
      })
    }
    
    modalState.isOpen = true
    modalState.loading = false
  }

  function close() {
    modalState.isOpen = false
    if (modalState.onClose) {
      modalState.onClose()
    }
    // Reset callbacks
    modalState.onConfirm = undefined
    modalState.onCancel = undefined
    modalState.onClose = undefined
  }

  async function confirm() {
    if (modalState.onConfirm) {
      modalState.loading = true
      try {
        await modalState.onConfirm()
        close()
      } catch (error) {
        console.error('Modal confirm error:', error)
        modalState.loading = false
      }
    } else {
      close()
    }
  }

  function cancel() {
    if (modalState.onCancel) {
      modalState.onCancel()
    }
    close()
  }

  // Convenience methods
  function showError(message: string, title = 'Error') {
    show({
      title,
      message,
      variant: 'error',
      icon: 'x',
      showCancel: false,
    })
  }

  function showSuccess(message: string, title = 'Success') {
    show({
      title,
      message,
      variant: 'success',
      icon: 'check-circle',
      showCancel: false,
    })
  }

  function showWarning(message: string, title = 'Warning') {
    show({
      title,
      message,
      variant: 'warning',
      icon: 'bolt',
      showCancel: false,
    })
  }

  function showInfo(message: string, title = 'Information') {
    show({
      title,
      message,
      variant: 'info',
      icon: 'check',
      showCancel: false,
    })
  }

  function showConfirm(
    message: string,
    onConfirm: () => void | Promise<void>,
    options?: Partial<ModalOptions>
  ) {
    show({
      title: options?.title || 'Confirm',
      message,
      variant: options?.variant || 'default',
      icon: options?.icon,
      onConfirm,
      ...options,
    })
  }

  return {
    modalState,
    show,
    close,
    confirm,
    cancel,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    showConfirm,
  }
}

